// rum-service.ts
// Every Datadog RUM call the app makes goes through rumService.
//
// Only type imports besides the SDK: the auth store imports this, so loading
// rum.config / rum-privacy (which load @/constant) here would pull them into
// the store. The boundary passes the init config in instead.
import { type RumInitConfiguration, datadogRum } from "@datadog/browser-rum";

import type { LoginCustomerData } from "@/types/authService";

export interface RumUserState {
  isAuthenticated?: boolean;
  customer: Pick<LoginCustomerData, "id" | "name" | "email"> | null;
}

export type LoginMethod = "password" | "otp" | "singpass";

// Which app sent the event: customer, retail, hq, risk-screening or region-admin.
// They share the RUM application (and, locally, the localhost session cookie).
export const RUM_APP = "customer";

// Datadog ends a session after 15 min idle or 4 h; end it after 30 min so a
// busy customer starts a new one at the top of the Sessions list.
export const MAX_RUM_SESSION_MS = 30 * 60 * 1000;
// When this tab first saw the shared _dd_s_v2 session, kept across reloads.
const SESSION_START_KEY = "dd-rum-session-start";
// The app this tab last reported. Same key as retail and region admin:
// locally every app runs on localhost and shares the session cookie and
// sessionStorage, so moving to another app in the same tab ends its session.
const TAB_SECTION_KEY = "dd-rum-section";

interface SessionStart {
  id?: string;
  at: number;
}

export function isRumEnabled(): boolean {
  return (
    Boolean(process.env.DD_RUM_APPLICATION_ID?.trim()) &&
    Boolean(process.env.DD_RUM_CLIENT_TOKEN?.trim())
  );
}

export class RumService {
  private sessionCapTimer: ReturnType<typeof setInterval> | undefined;
  private pendingSessionStop: ReturnType<typeof setTimeout> | undefined;

  isEnabled(): boolean {
    return isRumEnabled();
  }

  // The SDK is a page-wide singleton: init once, however often the boundary mounts.
  start(config: RumInitConfiguration): void {
    if (!this.isEnabled() || datadogRum.getInitConfiguration()) {
      return;
    }
    datadogRum.init(config);
    datadogRum.setGlobalContextProperty("app", RUM_APP);
    datadogRum.startSessionReplayRecording();
    this.startSessionCap();
  }

  // Starts a new session when this tab last ran another app (or retail section);
  // restoreUser() sets this app's user right after.
  enterApp(): void {
    if (!this.isEnabled()) {
      return;
    }
    const tabSection = readStorage(() => sessionStorage.getItem(TAB_SECTION_KEY), null);
    if (tabSection && tabSection !== RUM_APP) {
      datadogRum.clearUser();
      this.stopSessionOnceStarted();
    }
    writeStorage(() => sessionStorage.setItem(TAB_SECTION_KEY, RUM_APP));
  }

  // Follows the auth store: sets the user after login, a refresh or in a new
  // tab, and ends the session once the store is logged out (also from another tab).
  restoreUser(auth: RumUserState): void {
    if (!this.isEnabled()) {
      return;
    }
    if (auth.isAuthenticated && auth.customer) {
      if (datadogRum.getUser().id !== String(auth.customer.id)) {
        this.setUser(auth);
      }
    } else {
      this.resetSession();
    }
  }

  setUser({ customer }: RumUserState): void {
    if (!this.isEnabled() || !customer) {
      return;
    }
    datadogRum.setUser({
      id: String(customer.id),
      name: customer.name,
      email: customer.email,
    });
  }

  // Ends the logged-in user's session. Datadog starts the next one on the next
  // activity (the login page) and the next login joins it.
  resetSession(): void {
    if (!this.isEnabled() || !datadogRum.getUser().id) {
      return;
    }
    datadogRum.clearUser();
    datadogRum.stopSession();
  }

  startView(viewName: string): void {
    if (!this.isEnabled()) {
      return;
    }
    datadogRum.startView({ name: viewName });
  }

  // Only the failure shape is sent: never the typed email, password, OTP or the
  // backend message, which can echo the email back.
  trackLoginFailure(method: LoginMethod, error: unknown): void {
    if (!this.isEnabled()) {
      return;
    }
    const response = (error as { response?: { status?: number; data?: { code?: unknown } } })
      ?.response;
    const code = response?.data?.code;
    datadogRum.addAction("login_failed", {
      method,
      status: response?.status ?? null,
      code: typeof code === "string" || typeof code === "number" ? code : null,
    });
  }

  checkSessionAge(now = Date.now()): void {
    const id = datadogRum.getInternalContext()?.session_id;
    if (!id) {
      return; // Expired or stopped: the next activity starts a new one.
    }
    const start = readSessionStart();
    if (start?.id !== id) {
      writeStorage(() =>
        sessionStorage.setItem(SESSION_START_KEY, JSON.stringify({ id, at: now })),
      );
    } else if (now - start.at >= MAX_RUM_SESSION_MS) {
      datadogRum.stopSession(); // Keeps the user on the next session.
    }
  }

  private startSessionCap(): void {
    if (this.sessionCapTimer) {
      return;
    }
    // A new tab starts its own session; restoreUser() sets the user right
    // after. resetSession() would skip it, as a new tab has no RUM user yet.
    if (!readSessionStart()) {
      datadogRum.clearUser();
      this.stopSessionOnceStarted();
      writeStorage(() =>
        sessionStorage.setItem(SESSION_START_KEY, JSON.stringify({ at: Date.now() })),
      );
    }
    this.checkSessionAge();
    this.sessionCapTimer = setInterval(() => this.checkSessionAge(), 60 * 1000);
  }

  // init() starts RUM asynchronously and stopSession() is a no-op until then,
  // so a stop right after init would leave the tab in the cookie's session.
  // Stop it now if a session is running, else as soon as one is (up to 10 s).
  private stopSessionOnceStarted(): void {
    if (datadogRum.getInternalContext()?.session_id) {
      datadogRum.stopSession();
      return;
    }
    if (this.pendingSessionStop) {
      return;
    }
    const giveUpAt = Date.now() + 10 * 1000;
    const retry = (): void => {
      this.pendingSessionStop = undefined;
      if (datadogRum.getInternalContext()?.session_id) {
        datadogRum.stopSession();
      } else if (Date.now() < giveUpAt) {
        this.pendingSessionStop = setTimeout(retry, 250);
      }
    };
    this.pendingSessionStop = setTimeout(retry, 250);
  }
}

export const rumService = new RumService();

// null: nothing recorded, i.e. a new tab. Blocked storage counts as recorded,
// so a tab that can't remember is never reset (and never capped).
function readSessionStart(): SessionStart | null {
  return readStorage(
    () => {
      const stored = sessionStorage.getItem(SESSION_START_KEY);
      return stored ? (JSON.parse(stored) as SessionStart) : null;
    },
    { at: Date.now() },
  );
}

function readStorage<T>(read: () => T, fallback: T): T {
  try {
    return read();
  } catch {
    return fallback;
  }
}

function writeStorage(write: () => void): void {
  try {
    write();
  } catch {
    // Storage blocked: fall back to the shared-session behaviour.
  }
}
